import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
    Loader2,
    TrendingDown,
    TrendingUp,
    MoreHorizontal,
    Clock,
    History,
    Play,
    Square,
    Settings,
    Eye,
} from 'lucide-react';
import { OrderEditDialog } from './OrderEditDialog';
import { formatDate, formatInstrumentPrice } from '@/utils/formatters';
import { useSnackbar } from 'notistack';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// API Hooks
import {
    useTrades,
    useCloseTrade,
    useUpdateTrade,
    usePartialCloseTrade,
} from '@/api/trades/use-trades';
import { Trade } from '@/models/trade';

interface Order {
    id: number;
    pair: string;
    type: string;
    amount: number;
    price: number;
    status: string;
    time: string;
}

interface TradingActivityProps {
    orders?: Order[];
    defaultTab?: 'orders' | 'positions' | 'history';
}

export const TradingActivity = ({ orders, defaultTab = 'orders' }: TradingActivityProps) => {
    const { t } = useTranslation('trading');
    const { enqueueSnackbar } = useSnackbar();

    // State for dialogs and actions
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<any>(null);

    // API Hooks - using unified Trade model
    const {
        data: pendingTradesData,
        isLoading: pendingLoading,
        refetch: refetchPending,
    } = useTrades({
        status: 'pending',
        per_page: 50,
    });

    const {
        data: openTradesData,
        isLoading: positionsLoading,
        refetch: refetchPositions,
    } = useTrades({
        status: 'open',
        per_page: 50,
    });

    const {
        data: closedTradesData,
        isLoading: historyLoading,
        refetch: refetchHistory,
    } = useTrades({
        status: 'closed',
        per_page: 50,
    });

    const pendingOrders = pendingTradesData?.trades || [];
    const openPositions = openTradesData?.trades || [];
    const orderHistory = closedTradesData?.trades || [];

    // Mutation hooks for actions
    const closeTradeMutation = useCloseTrade();
    const updateTradeMutation = useUpdateTrade();
    const partialCloseTradeMutation = usePartialCloseTrade();

    // Enhanced status colors with better visual hierarchy
    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'filled':
            case 'completed':
                return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30 dark:bg-emerald-500/20 dark:text-emerald-400';
            case 'pending':
            case 'partial':
            case 'open':
                return 'bg-amber-500/10 text-amber-600 border-amber-500/30 dark:bg-amber-500/20 dark:text-amber-400';
            case 'cancelled':
            case 'rejected':
            case 'failed':
                return 'bg-red-500/10 text-red-600 border-red-500/30 dark:bg-red-500/20 dark:text-red-400';
            case 'active':
            case 'running':
                return 'bg-primary/10 text-primary border-primary/30 dark:bg-primary/20 dark:text-primary';
            default:
                return 'bg-muted-foreground/10 text-muted-foreground border-muted-foreground/30 dark:bg-muted-foreground/20 dark:text-muted-foreground';
        }
    };

    const getSideColor = (side: string) => {
        return side?.toLowerCase() === 'buy'
            ? 'text-emerald-600 bg-emerald-50 border-emerald-200 dark:text-emerald-400 dark:bg-emerald-950/30 dark:border-emerald-800'
            : 'text-red-600 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-950/30 dark:border-red-800';
    };

    const getPnLColor = (pnl: number) => {
        if (pnl > 0) return 'text-emerald-600 dark:text-emerald-400';
        if (pnl < 0) return 'text-red-600 dark:text-red-400';
        return 'text-muted-foreground dark:text-muted-foreground';
    };

    const formatAmount = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
        }).format(amount);
    };

    const formatPrice = (
        price: number,
        instrument?: { symbol?: string; quote_precision?: number } | null
    ) => {
        return formatInstrumentPrice(price, instrument);
    };

    const formatPnL = (pnl: number) => {
        const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        }).format(Math.abs(pnl));

        return pnl >= 0 ? `+${formatted}` : `-${formatted}`;
    };

    // Enhanced action handlers - using unified Trade model
    const handleClosePosition = async (positionId: string) => {
        try {
            await closeTradeMutation.mutateAsync({
                id: positionId,
                data: {
                    reason: 'manual',
                },
            });
            enqueueSnackbar('Position closed successfully', { variant: 'success' });
            refetchPositions();
        } catch (error) {
            enqueueSnackbar('Failed to close position', { variant: 'error' });
        }
    };

    // Modify SL/TP dialog state
    const [modifyDialogOpen, setModifyDialogOpen] = useState(false);
    const [modifyType, setModifyType] = useState<'stop_loss' | 'take_profit'>('stop_loss');
    const [modifyForPosition, setModifyForPosition] = useState<any>(null);
    const [modifyPrice, setModifyPrice] = useState<string>('');

    // Partial close dialog state
    const [partialCloseDialogOpen, setPartialCloseDialogOpen] = useState(false);
    const [partialClosePosition, setPartialClosePosition] = useState<any>(null);
    const [partialCloseLots, setPartialCloseLots] = useState<string>('');

    const openModifyDialog = (position: any, type: 'stop_loss' | 'take_profit') => {
        setModifyForPosition(position);
        setModifyType(type);
        setModifyPrice(
            type === 'stop_loss'
                ? position?.stop_loss_price?.toString() || ''
                : position?.take_profit_price?.toString() || ''
        );
        setModifyDialogOpen(true);
    };

    const openPartialCloseDialog = (position: any) => {
        setPartialClosePosition(position);
        setPartialCloseLots('');
        setPartialCloseDialogOpen(true);
    };

    const handlePartialClose = async () => {
        if (!partialClosePosition) return;

        const lots = parseFloat(partialCloseLots);
        if (!lots || lots <= 0) {
            enqueueSnackbar('Please enter a valid number of lots', { variant: 'error' });
            return;
        }

        if (lots >= partialClosePosition.lot_size) {
            enqueueSnackbar('Partial close lots must be less than the total lot size', {
                variant: 'error',
            });
            return;
        }

        try {
            await partialCloseTradeMutation.mutateAsync({
                id: partialClosePosition.id,
                data: {
                    close_lots: lots,
                    reason: 'partial_close',
                },
            });

            enqueueSnackbar('Position partially closed successfully', { variant: 'success' });
            setPartialCloseDialogOpen(false);
            setPartialClosePosition(null);
            setPartialCloseLots('');
            refetchPositions();
        } catch (error) {
            enqueueSnackbar('Failed to partially close position', { variant: 'error' });
        }
    };

    const submitModify = async () => {
        if (!modifyForPosition) return;
        const id = modifyForPosition.id;
        const value = modifyPrice ? parseFloat(modifyPrice) : null;

        try {
            const updateData: any = {};
            if (modifyType === 'stop_loss') {
                updateData.stop_loss_price = value;
            } else {
                updateData.take_profit_price = value;
            }

            await updateTradeMutation.mutateAsync({
                id,
                data: updateData,
            });

            enqueueSnackbar(
                `${modifyType === 'stop_loss' ? 'Stop loss' : 'Take profit'} updated successfully`,
                { variant: 'success' }
            );
            setModifyDialogOpen(false);
            setModifyForPosition(null);
            setModifyPrice('');
            refetchPositions();
        } catch (e: any) {
            const message = e?.response?.data?.message || 'Failed to update';
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    const handleEditOrder = (order: any) => {
        setSelectedOrder(order);
        setEditDialogOpen(true);
    };

    const handleOrderUpdated = () => {
        setEditDialogOpen(false);
        setSelectedOrder(null);
        refetchPending();
        refetchHistory();
    };

    // Calculate totals for badges
    const activePositions = openPositions.length;
    const activeOrders = pendingOrders;
    const pendingCount = activeOrders.length;

    // Filter history - all closed trades are history
    const completedHistory = orderHistory;

    return (
        <Card className="bg-card border-border h-full shadow-sm">
            <CardContent className="p-0 h-full">
                <Tabs defaultValue={defaultTab} className="h-full">
                    <div className="px-4 pb-2 pt-2">
                        <TabsList className="grid w-full grid-cols-3 bg-muted/50 h-8">
                            <TabsTrigger
                                value="orders"
                                className="flex items-center gap-1 data-[state=active]:bg-background data-[state=active]:shadow-sm text-xs"
                            >
                                <Clock className="h-3 w-3" />
                                {t('ordersTab')}
                                <Badge variant="secondary" className="ml-1 h-4 px-1 text-xs">
                                    {pendingCount}
                                </Badge>
                            </TabsTrigger>
                            <TabsTrigger
                                value="positions"
                                className="flex items-center gap-1 data-[state=active]:bg-background data-[state=active]:shadow-sm text-xs"
                            >
                                <Play className="h-3 w-3" />
                                {t('positionsTab')}
                                <Badge variant="secondary" className="ml-1 h-4 px-1 text-xs">
                                    {activePositions}
                                </Badge>
                            </TabsTrigger>
                            <TabsTrigger
                                value="history"
                                className="flex items-center gap-1 data-[state=active]:bg-background data-[state=active]:shadow-sm text-xs"
                            >
                                <History className="h-3 w-3" />
                                {t('historyTab')}
                                <Badge variant="secondary" className="ml-1 h-4 px-1 text-xs">
                                    {completedHistory.length}
                                </Badge>
                            </TabsTrigger>
                        </TabsList>
                    </div>

                    {/* Orders Tab */}
                    <TabsContent value="orders" className="h-[calc(100%-40px)] m-0 px-4">
                        {pendingLoading ? (
                            <div className="flex items-center justify-center h-32">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                <span className="ml-2 text-muted-foreground">
                                    {t('loadingOrders')}
                                </span>
                            </div>
                        ) : !activeOrders.length ? (
                            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                                <Clock className="h-8 w-8 mb-2 opacity-50" />
                                <p className="text-sm">{t('noPendingOrders')}</p>
                                <p className="text-xs opacity-75">{t('pendingOrdersAppearHere')}</p>
                            </div>
                        ) : (
                            <ScrollArea className="h-full">
                                <div className="overflow-x-auto">
                                    <div className="min-w-[800px]">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="border-border hover:bg-transparent">
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        SYMBOL
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        TYPE
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        SIDE
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        SIZE
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        PRICE
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        STATUS
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        TIME
                                                    </TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {activeOrders.map((order: Trade) => (
                                                    <TableRow
                                                        key={order.id}
                                                        className="border-border hover:bg-accent/30 transition-colors cursor-pointer"
                                                        onClick={() => handleEditOrder(order)}
                                                    >
                                                        <TableCell className="text-foreground text-sm font-medium">
                                                            {order.instrument?.symbol || 'N/A'}
                                                        </TableCell>
                                                        <TableCell className="text-foreground text-sm">
                                                            {order.type?.toUpperCase()}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge
                                                                variant="outline"
                                                                className={`text-xs font-medium ${getSideColor(order.side)}`}
                                                            >
                                                                {order.side?.toUpperCase()}
                                                                {order.side === 'buy' ? (
                                                                    <TrendingUp className="w-3 h-3 ml-1" />
                                                                ) : (
                                                                    <TrendingDown className="w-3 h-3 ml-1" />
                                                                )}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-foreground text-sm font-mono">
                                                            {formatAmount(order.lot_size || 0)}
                                                        </TableCell>
                                                        <TableCell className="text-foreground text-sm font-mono">
                                                            {formatPrice(
                                                                order.entry_price || 0,
                                                                order.instrument
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge
                                                                variant="outline"
                                                                className={`text-xs font-medium ${getStatusColor(order.status)}`}
                                                            >
                                                                {order.status?.toUpperCase()}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-muted-foreground text-xs">
                                                            {formatDate(order.created_at)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            </ScrollArea>
                        )}
                    </TabsContent>

                    {/* Positions Tab */}
                    <TabsContent value="positions" className="h-[calc(100%-40px)] m-0 px-4">
                        {positionsLoading ? (
                            <div className="flex items-center justify-center h-32">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                <span className="ml-2 text-muted-foreground">
                                    Loading positions...
                                </span>
                            </div>
                        ) : !openPositions.length ? (
                            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                                <Square className="h-8 w-8 mb-2 opacity-50" />
                                <p className="text-sm">No open positions</p>
                                <p className="text-xs opacity-75">
                                    Your active trades will appear here
                                </p>
                            </div>
                        ) : (
                            <ScrollArea className="h-full">
                                <div className="overflow-x-auto">
                                    <div className="min-w-[800px]">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="border-border hover:bg-transparent">
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        SYMBOL
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        SIDE
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        SIZE
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        ENTRY
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        CURRENT
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        P&L
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        MARGIN
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        ACTIONS
                                                    </TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {openPositions.map((position: Trade) => (
                                                    <TableRow
                                                        key={position.id}
                                                        className="border-border hover:bg-accent/30 transition-colors"
                                                    >
                                                        <TableCell className="text-foreground text-sm font-medium">
                                                            {position.instrument?.symbol || 'N/A'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge
                                                                variant="outline"
                                                                className={`text-xs font-medium ${getSideColor(position.side)}`}
                                                            >
                                                                {position.side?.toUpperCase()}
                                                                {position.side === 'buy' ? (
                                                                    <TrendingUp className="w-3 h-3 ml-1" />
                                                                ) : (
                                                                    <TrendingDown className="w-3 h-3 ml-1" />
                                                                )}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-foreground text-sm font-mono">
                                                            {formatAmount(position.lot_size || 0)}
                                                        </TableCell>
                                                        <TableCell className="text-foreground text-sm font-mono">
                                                            {formatPrice(
                                                                position.entry_price || 0,
                                                                position.instrument
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-foreground text-sm font-mono">
                                                            {formatPrice(
                                                                position.current_price || 0,
                                                                position.instrument
                                                            )}
                                                        </TableCell>
                                                        <TableCell
                                                            className={`text-sm font-mono ${getPnLColor(position.unrealized_pnl || 0)}`}
                                                        >
                                                            {formatPnL(
                                                                position.unrealized_pnl || 0
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-foreground text-sm font-mono">
                                                            {formatAmount(
                                                                position.margin_used || 0
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-8 w-8 p-0"
                                                                    >
                                                                        <MoreHorizontal className="h-4 w-4" />
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    <DropdownMenuItem
                                                                        onClick={() =>
                                                                            handleClosePosition(
                                                                                position.id
                                                                            )
                                                                        }
                                                                    >
                                                                        <Square className="h-4 w-4 mr-2" />
                                                                        Close Position
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem
                                                                        onClick={() =>
                                                                            openPartialCloseDialog(
                                                                                position
                                                                            )
                                                                        }
                                                                    >
                                                                        <Square className="h-4 w-4 mr-2" />
                                                                        Partial Close
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem
                                                                        onClick={() =>
                                                                            openModifyDialog(
                                                                                position,
                                                                                'stop_loss'
                                                                            )
                                                                        }
                                                                    >
                                                                        <Settings className="h-4 w-4 mr-2" />
                                                                        Modify Stop Loss
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem
                                                                        onClick={() =>
                                                                            openModifyDialog(
                                                                                position,
                                                                                'take_profit'
                                                                            )
                                                                        }
                                                                    >
                                                                        <Settings className="h-4 w-4 mr-2" />
                                                                        Modify Take Profit
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuSeparator />
                                                                    <DropdownMenuItem>
                                                                        <Eye className="h-4 w-4 mr-2" />
                                                                        View Details
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            </ScrollArea>
                        )}
                    </TabsContent>

                    {/* History Tab */}
                    <TabsContent value="history" className="h-[calc(100%-40px)] m-0 px-4">
                        {historyLoading ? (
                            <div className="flex items-center justify-center h-32">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                <span className="ml-2 text-muted-foreground">
                                    Loading history...
                                </span>
                            </div>
                        ) : !completedHistory.length ? (
                            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                                <History className="h-8 w-8 mb-2 opacity-50" />
                                <p className="text-sm">No trading history</p>
                                <p className="text-xs opacity-75">
                                    Your completed trades will appear here
                                </p>
                            </div>
                        ) : (
                            <ScrollArea className="h-full">
                                <div className="overflow-x-auto">
                                    <div className="min-w-[800px]">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="border-border hover:bg-transparent">
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        SYMBOL
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        TYPE
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        SIDE
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        SIZE
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        PRICE
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        P&L
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        STATUS
                                                    </TableHead>
                                                    <TableHead className="text-muted-foreground text-xs font-medium">
                                                        TIME
                                                    </TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {completedHistory.map((order: Trade) => (
                                                    <TableRow
                                                        key={order.id}
                                                        className="border-border hover:bg-accent/30 transition-colors"
                                                    >
                                                        <TableCell className="text-foreground text-sm font-medium">
                                                            {order.instrument?.symbol || 'N/A'}
                                                        </TableCell>
                                                        <TableCell className="text-foreground text-sm">
                                                            {order.type?.toUpperCase()}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge
                                                                variant="outline"
                                                                className={`text-xs font-medium ${getSideColor(order.side)}`}
                                                            >
                                                                {order.side?.toUpperCase()}
                                                                {order.side === 'buy' ? (
                                                                    <TrendingUp className="w-3 h-3 ml-1" />
                                                                ) : (
                                                                    <TrendingDown className="w-3 h-3 ml-1" />
                                                                )}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-foreground text-sm font-mono">
                                                            {formatAmount(order.lot_size || 0)}
                                                        </TableCell>
                                                        <TableCell className="text-foreground text-sm font-mono">
                                                            {formatPrice(
                                                                order.entry_price || 0,
                                                                order.instrument
                                                            )}
                                                        </TableCell>
                                                        <TableCell
                                                            className={`text-sm font-mono ${getPnLColor(order.realized_pnl || 0)}`}
                                                        >
                                                            {formatPnL(order.realized_pnl || 0)}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge
                                                                variant="outline"
                                                                className={`text-xs font-medium ${getStatusColor(order.status)}`}
                                                            >
                                                                {order.status?.toUpperCase()}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-muted-foreground text-xs">
                                                            {formatDate(order.created_at)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            </ScrollArea>
                        )}
                    </TabsContent>
                </Tabs>
            </CardContent>

            {/* Order Edit Dialog */}
            {editDialogOpen && selectedOrder && (
                <OrderEditDialog
                    isOpen={editDialogOpen}
                    onClose={() => {
                        setEditDialogOpen(false);
                        setSelectedOrder(null);
                    }}
                    order={selectedOrder}
                    onOrderUpdated={handleOrderUpdated}
                />
            )}

            {/* Modify SL/TP Dialog */}
            <Dialog open={modifyDialogOpen} onOpenChange={setModifyDialogOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>
                            {modifyType === 'stop_loss' ? 'Modify Stop Loss' : 'Modify Take Profit'}
                        </DialogTitle>
                        <DialogDescription>
                            Update {modifyType === 'stop_loss' ? 'stop loss' : 'take profit'} price
                            for
                            {` ${modifyForPosition?.instrument?.symbol || ''}`}.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Label htmlFor="modify-price">
                            {modifyType === 'stop_loss' ? 'Stop Loss' : 'Take Profit'} Price
                        </Label>
                        <Input
                            id="modify-price"
                            type="number"
                            step="0.00001"
                            min="0"
                            value={modifyPrice}
                            onChange={e => setModifyPrice(e.target.value)}
                            placeholder={`Enter ${modifyType === 'stop_loss' ? 'stop loss' : 'take profit'} price`}
                        />
                        <p className="text-xs text-muted-foreground">
                            Leave empty to remove{' '}
                            {modifyType === 'stop_loss' ? 'stop loss' : 'take profit'}.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setModifyDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={submitModify} disabled={updateTradeMutation.isPending}>
                            {updateTradeMutation.isPending && (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            )}
                            Update
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
};
